import { Component, Inject, OnInit, WritableSignal, signal } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialog } from '@angular/material/dialog';
import { MatOptionSelectionChange } from '@angular/material/core';
import { debounceTime, switchMap } from 'rxjs';
import { MatterService } from '../shared/services/matter.service';
import { CommonService } from '../shared/services/common.service';
import { SnackbarService } from '../shared/services/snackbar.service';
import { Matter } from '../shared/models/matter';
import { AttachmentData, RequestDocAttachmentResponse } from '../shared/models/requestDocAttachment';
import { RequestsService } from '../shared/services/requests.service';
import { JournalResponseDto } from '../shared/models/journalResponseDto';
import { ApprovalDetails } from '../shared/models/ApprovalDetails';
import { RequestType, RequestTypeOrder } from '../enums/request-type.enum';
import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';
import { UserRequestsService } from '../shared/services/user-requests.service';
import { Participants } from '../shared/models/participants';
import { BankAccountService } from '../shared/services/bank-account.service';
import { BankAccount } from '../shared/models/bankAccount';
import { validateJournalTransfer } from '../shared/utils/journal-validation';
import { AuditService } from '../shared/services/audit.service';

@Component({
    selector: 'xp-view-user-journal',
    templateUrl: './view-user-journal.component.html',
    styleUrl: './view-user-journal.component.scss'
})
export class ViewUserJournalComponent implements OnInit {
    journalRequestForm!: FormGroup;
    currentDate = new Date();
    isLoading = false;
    noData = false;
    submitBtn = false;

    matterFromDropdownData: Matter[] = [];
    matterToDropdownData: Matter[] = [];
    isMatterToLoading = false;
    displayValue: string | null = '';
    displayFile: string[] = [];
    fileNames: AttachmentData[] = [];
    showReturnReason: boolean = false;
    isDisabled = false;
    isRejectDisabled = false;
    isWithdrawDisabled = false;
    actionstepMatterUrl1: string = '';
    showLinkToOpenMatter1: WritableSignal<boolean> = signal(false);
    actionstepMatterUrl2: string = '';
    showLinkToOpenMatter2: WritableSignal<boolean> = signal(false);
    matterFromNo: string = '';
    matterToNo: string = '';
    actionstepBaseUrl: string = '';
    filteredBankAccounts: BankAccount[] = [];
    actionstepMatterOverviewUrl1: string = '';
    actionstepMatterOverviewUrl2: string = '';
    actionstepOverviewBaseUrl: string = '';
    bankAccounts: BankAccount[] = [];
    selectedBankAccountId1: string = '';
    selectedBankAccountId2: string = '';
    showDateTransaction: WritableSignal<boolean> = signal(false);

    constructor(
        private fb: FormBuilder,
        private matterService: MatterService,
        private commonService: CommonService,
        private snackbarService: SnackbarService,
        private requestService: RequestsService,
        private dialogRef: MatDialogRef<ViewUserJournalComponent>,
        private dialog: MatDialog,
        private userRequestService: UserRequestsService,
        private bankAccountService: BankAccountService,
        private auditService: AuditService,
        @Inject(MAT_DIALOG_DATA) public journal: JournalResponseDto
    ) { }

    async ngOnInit() {
        this.initializeForm();

        this.actionstepBaseUrl = await this.requestService.getActionstepUrl();
        this.actionstepOverviewBaseUrl = await this.requestService.getActionStepOverviewUrl();
        this.bankAccounts = await this.bankAccountService.getAllBankAccounts();
        await this.filterBankAccounts();

        this.journalRequestForm.get('clientBankAccount1')!.valueChanges.subscribe((account: BankAccount | null) => {
            this.selectedBankAccountId1 = account?.accountId ?? '';
            if (this.matterFromNo) {
                this.actionstepMatterUrl1 = `${this.actionstepBaseUrl}?action_id=${this.matterFromNo}&ac_account_id=${this.selectedBankAccountId1}`;
            }
        });

        this.journalRequestForm.get('clientBankAccount2')!.valueChanges.subscribe((account: BankAccount | null) => {
            this.selectedBankAccountId2 = account?.accountId ?? '';
            if (this.matterToNo) {
                this.actionstepMatterUrl2 = `${this.actionstepBaseUrl}?action_id=${this.matterToNo}&ac_account_id=${this.selectedBankAccountId2}`;
            }
        });

        this.setupAutocomplete();
        if (this.journal) {
            this.patchFormValues(this.journal);
            this.toggleFormFieldsByStage();
        }
    }

    initializeForm() {
        this.journalRequestForm = this.fb.group({
            returnReason: [''],
            matterFrom: [null, Validators.required],
            matterTo: [null, Validators.required],
            reason: ['', Validators.required],
            amount: [null, Validators.required],
            dateRequested: [null, Validators.required],
            requestedTime: [{ value: '' }],
            uploadedFiles: [[]],
            clientBankAccount1: ['', Validators.required],
            clientBankAccount2: ['', Validators.required],
            dateTransaction: '',
            transactionTime:''
        });
    }

    async patchFormValues(journal: JournalResponseDto) {
        this.journalRequestForm.patchValue({
            returnReason: journal.returnReason,
            matterFrom: journal.matterFrom,
            matterTo: journal.matterTo,
            reason: journal.reason,
            amount: journal.amount,
            dateRequested: journal.dateRequested ? new Date(journal.dateRequested) : null,
            clientBankAccount1: this.resolveBankAccount(journal.clientBankAccountId1) ?? null,
            clientBankAccount2: this.resolveBankAccount(journal.clientBankAccountId2) ?? null,
            dateTransaction: journal.dateTransaction
        });

        if (journal.dateTransaction) {
            const d = new Date(journal.dateTransaction);
            this.journalRequestForm.get('transactionTime')?.setValue(this.toTimeString(d));
            this.showDateTransaction.set(true);
        }
        else {
            this.showDateTransaction.set(false);
        }

        this.matterFromNo = journal.matterFromNo.toString();
        this.matterToNo = journal.matterToNo.toString();

        if (this.matterToNo) {
            this.actionstepMatterUrl2 = `${this.actionstepBaseUrl}?action_id=${this.matterToNo}&ac_account_id=${this.selectedBankAccountId2}`;
            this.actionstepMatterOverviewUrl2 = `${this.actionstepOverviewBaseUrl}/${this.matterToNo}`;
            this.showLinkToOpenMatter2.set(true);
        }

        if (this.matterFromNo) {
            this.actionstepMatterUrl1 = `${this.actionstepBaseUrl}?action_id=${this.matterFromNo}&ac_account_id=${this.selectedBankAccountId1}`;
            this.actionstepMatterOverviewUrl1 = `${this.actionstepOverviewBaseUrl}/${this.matterFromNo}`;
            this.showLinkToOpenMatter1.set(true);
            await this.getMatterByClient(this.matterFromNo);
        }

        if (journal.dateRequested) {
            const d = new Date(journal.dateRequested);
            this.journalRequestForm.get('requestedTime')?.setValue(this.toTimeString(d));
        }
    }

    setupAutocomplete() {
        this.journalRequestForm.controls['matterFrom'].valueChanges.pipe(
            debounceTime(300),
            switchMap((query) => this.fetchMatterList(query))
        ).subscribe({
            next: (data) => {
                this.matterFromDropdownData = data;
                this.noData = !data?.length;
                this.isLoading = false;
            },
            error: (error) => {
                this.noData = true;
                if (error.status == 401) {
                    this.commonService.apiSecurityError();
                }
            },
            complete: () => {
                this.isLoading = false;
            }
        });

    }

    fetchMatterList(query: string) {
        let id = '';
        let name = '';
        if (!isNaN(Number(query))) {
            id = query;
        } else {
            name = query;
        }
        this.isLoading = true;
        this.noData = false;
        return this.matterService.getMatterList(id, name);
    }

    async lookupSelecttionChangd(event: MatOptionSelectionChange<string>, controlName: string) {
        if (!event.isUserInput) return;
        const res = event.source.viewValue;
        if (!res || !res.includes(' - ')) return;

        const [matterNo, description] = res.split(' - ');
        this.journalRequestForm.get(controlName)?.setValue(description);

        controlName === 'matterFrom' ? this.matterFromNo = matterNo : this.matterToNo = matterNo;

        if (controlName === 'matterFrom') {
            this.actionstepMatterUrl1 = `${this.actionstepBaseUrl}?action_id=${this.matterFromNo}&ac_account_id=${this.selectedBankAccountId1}`;
            this.showLinkToOpenMatter1.set(true);
            this.resetMatterTo();
            this.journalRequestForm.get('matterTo')?.enable();

            await this.getMatterByClient(matterNo);
        } else {
            this.actionstepMatterUrl2 = `${this.actionstepBaseUrl}?action_id=${this.matterToNo}&ac_account_id=${this.selectedBankAccountId2}`;
            this.showLinkToOpenMatter2.set(true);
        }
    }

    async clearSearchFilter(matter: string) {
        if (matter == 'from') {
            this.showLinkToOpenMatter1.set(false);
            this.actionstepMatterUrl1 = '';
            this.journalRequestForm.controls.matterFrom.reset();
            this.matterFromDropdownData = [];
            this.showLinkToOpenMatter2.set(false);
            this.actionstepMatterUrl2 = '';
            this.journalRequestForm.controls.matterTo.reset();
            this.matterToDropdownData = [];
            this.journalRequestForm.get('matterTo')?.disable();
        }
        else {
            this.resetMatterTo();
        }
    }

    private resolveBankAccount(accountId?: string): BankAccount | undefined {
        return this.bankAccounts.find(a => a.accountId === accountId);
    }

    private toTimeString(date: Date): string {
        const pad = (n: number) => String(n).padStart(2, '0');
        return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
    }

    validateInput(event: KeyboardEvent): void {
        const allowedKeys = /[0-9.,]/; // Only allow numbers, dot, and comma
        const key = event.key;

        // Allow control keys like Backspace, Arrow keys, Delete, Tab, etc.
        if (['Backspace', 'ArrowLeft', 'ArrowRight', 'Tab', 'Delete'].includes(key)) {
            return;
        }

        if (!allowedKeys.test(key)) {
            event.preventDefault();
        }
    }

    onFilesSelected(input: any) {
        if (!input.files) return;

        this.fileNames = [];
        this.displayFile = [];

        const filesArray = Array.from(input.files);
        let filesProcessed = 0;

        filesArray.forEach((file: any) => {
            const fileName = file.name;
            this.displayFile.push(fileName);

            const reader = new FileReader();
            reader.onload = (e: any) => {
                const bytes = e.target.result.split('base64,')[1];
                this.fileNames.push({ fileName: fileName, attachments: bytes });

                filesProcessed++;
                if (filesProcessed === filesArray.length) {
                    this.journalRequestForm.get('uploadedFiles')?.setValue(this.fileNames);
                    this.journalRequestForm.get('uploadedFiles')?.updateValueAndValidity();
                }
            };
            reader.readAsDataURL(file);
        });
    }

    clearUpload() {
        this.displayFile = [];
        this.fileNames = [];
        this.journalRequestForm.get('uploadedFiles')?.setValue([]);
    }
    private toggleFormFieldsByStage() {
        const isParalegalStage = this.journal.currentStage?.stageOrder === 2;
        const isPartnerStage = (this.journal.currentStage?.stageOrder === 3 && this.journal.isCreatorPartnerApprove) ?? false;

        this.isRejectDisabled = this.journal.currentStage?.stageOrder === 1 || (this.journal.currentStage?.stageOrder ?? 0) >= 5;
        this.isWithdrawDisabled = this.journal.currentStage?.stageOrder === 1 || this.journal.currentStage?.stageOrder === 7;

        this.isDisabled = !(isParalegalStage || isPartnerStage);
        const isEditable = isParalegalStage || isPartnerStage;

        Object.keys(this.journalRequestForm.controls).forEach(controlName => {
            const control = this.journalRequestForm.get(controlName);
            if (isEditable) {
                control?.enable();
            } else {
                control?.disable();
            }
        });
        this.showReturnReason = isEditable;

        this.journalRequestForm.get('dateRequested')?.disable();
        this.journalRequestForm.get('requestedTime')?.disable();

    }

    uploadDoc() {
        if (this.journalRequestForm.get('uploadedFiles')?.value.length > 0) {
            let docData: RequestDocAttachmentResponse = {
                requestId: this.journal.id,
                requestType: RequestType.Journal,
                attachments: this.journalRequestForm.get('uploadedFiles')?.value
            }

            this.requestService.addRequestDocument(docData).then((res) => {
                this.snackbarService.successSnackBar(`${res}`);
            })
        }
        this.dialogRef.close(true);
    }

    filterBankAccounts() {
        this.filteredBankAccounts = [...this.bankAccounts];
    }

    delete() {
        var configOk = {
            title: 'Are you sure?',
            message: 'The data associated with the request will be deleted.',
            dialogType: 'warning',
            buttonType: 'ConfirmCancel',
            confirmText: 'Confirm',
            cancelText: 'Cancel',
        }
        const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            data: configOk,
            width: '30%',
        });

        dialogRef.afterClosed().subscribe(res => {
            if (res) {
                this.requestService.deleteJournalRequest(this.journal.id)
                    .then((res) => {
                        if (res) {
                            this.snackbarService.successSnackBar(`${res}`);
                            this.dialogRef.close(true);
                        }
                    })
                    .catch((error) => {
                        this.snackbarService.errorSnackBar("Failed to delete journal request. Please try again.");
                    });
            }
        })
    }

    async approveRequest() {
        if (this.journalRequestForm.invalid) {
            this.journalRequestForm.markAllAsTouched();
            return;
        }

        const isPartnerStage = (this.journal.currentStage?.stageOrder === 3 && this.journal.isCreatorPartnerApprove) ?? false;

        const selectedAccount1 = this.resolveBankAccount(this.selectedBankAccountId1);
        const selectedAccount2 = this.resolveBankAccount(this.selectedBankAccountId2);

        const transferError = validateJournalTransfer(
            Number(this.matterFromNo),
            Number(this.matterToNo),
            this.selectedBankAccountId1 || this.journal.clientBankAccountId1,
            this.selectedBankAccountId2 || this.journal.clientBankAccountId2
        );

        if (transferError) {
            this.snackbarService.errorSnackBar(transferError);
            return;
        }

        const approvalDetails: ApprovalDetails = {
            matter: this.journalRequestForm.value.matterFrom,
            matterTo: this.journalRequestForm.value.matterTo,
            matterFromNo: Number(this.matterFromNo),
            matterToNo: Number(this.matterToNo),
            reasonForJournal: this.journalRequestForm.value.reason,
            amount: this.journalRequestForm.value.amount,
            requestDate: this.journalRequestForm.value.dateRequested,
            clientBankAccountId: this.selectedBankAccountId1 || this.journal.clientBankAccountId1,
            clientBankAccountId2: this.selectedBankAccountId2 || this.journal.clientBankAccountId2,
            clientBankAccountName1: selectedAccount1
                ? `${selectedAccount1.accountId} - ${selectedAccount1.description}`
                : this.journal.clientBankAccountName1,
            clientBankAccountName2: selectedAccount2
                ? `${selectedAccount2.accountId} - ${selectedAccount2.description}`
                : this.journal.clientBankAccountName2,
        };

        if (isPartnerStage) {
            approvalDetails.fundsCheckedByPartner = true;
        }

        this.requestService.approveRequest(
            this.journal.id,
            RequestType.Journal,
            (this.journal.currentStage!.id) + 1,
            approvalDetails
        )
            .then((res: string) => {
                if (res) {
                    this.snackbarService.successSnackBar(`${res}`);
                    this.dialogRef.close(true);
                    this.uploadDoc();
                }
            })
            .catch((error) => {
                this.snackbarService.errorSnackBar("Failed to approve journal request. Please try again.");
            });
    }

    closeDialog() {
        this.dialogRef.close(true);
    }

    async withdrawJournal() {
        const audits = await this.auditService.getUserAuditByRequest(this.journal.id, RequestTypeOrder[RequestType.Journal]);
        let latestAudit = null;

        if (this.journal?.currentStage?.stageOrder == 5) {
            latestAudit = audits?.length ? audits[audits.length - 1] : null;
        } else {
            latestAudit = audits?.length ? audits[audits.length - 2] : null;
        }
        const rpaStatus = latestAudit?.wasRpaSuccessful ?? null;
        const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            width: '600px',
            data: {
                title: 'Withdraw Journal Request?',
                message: 'Are you sure you want to withdraw this journal request?',
                dialogType: 'warning',
                buttonType: 'ConfirmCancel',
                confirmText: 'Confirm',
                cancelText: 'Cancel',
                matterNo: this.journal?.matterFromNo,
                currentStageId: this.journal?.currentStage?.id,
                rpaStatus: rpaStatus,
                skipAccountsValidation: true
            }
        });

        dialogRef.afterClosed().subscribe(res => {
            if (res) {
                this.userRequestService.withdrawJournalRequest(this.journal.id)
                    .then((res) => {
                        if (res) {
                            this.snackbarService.successSnackBar(`${res}`);
                            this.dialogRef.close(true);
                        }
                    })
                    .catch((error) => {
                        this.snackbarService.errorSnackBar("Failed to withdraw journal request. Please try again.");
                    });
            }
        })
    }

    onUrlCopied() {
        this.snackbarService.successSnackBar("URL copied to clipboard");
    }

    async getMatterByClient(matterNo: string) {
        this.matterToDropdownData = [];
        this.isMatterToLoading = true;

        try {
            const participants: Participants[] = await this.matterService.getMatterClient(Number(matterNo), 27);

            const allMatters: Matter[] = [];
            for (const participant of participants ?? []) {
                const matters = await this.matterService.getMatterListByParticipant(participant.id);
                allMatters.push(...matters);
            }

            this.matterToDropdownData = allMatters.filter(
                m => m.number.toString() !== matterNo
            );
        } catch {
            this.snackbarService.errorSnackBar('Failed to load matters for the selected client.');
        } finally {
            this.isMatterToLoading = false;
        }
    }

    private resetMatterTo() {
        this.matterToNo = '';
        this.showLinkToOpenMatter2.set(false);
        this.actionstepMatterUrl2 = '';
        this.actionstepMatterOverviewUrl2 = '';
        this.journalRequestForm.controls.matterTo.reset();
    }
}
